// ---------------------------------------------------------------------------
// callScriptShared.payload
//
// Value-merging primitives shared by both Call Script save-payload builders.
//
// Both scripts follow the same rule when turning the modal's two columns into
// a request body: a non-blank "Verified Information" value wins, otherwise the
// saved value rides along (so a field the analyst never touched isn't blanked
// out server-side), otherwise the key is omitted entirely rather than sent as
// "" or null.
//
// Deliberately NOT here: each script's `toAnswerString` / `toAnswerText`.
// They look alike but employment phone-normalises its answers and residence
// does not — one of the open divergences to settle against the real residence
// swagger, not something to paper over with a shared helper.
// ---------------------------------------------------------------------------

/**
 * Maps a boolean onto the wire's Y/N flag.
 * @param {boolean} value - The flag's value.
 * @returns {"Y" | "N"} The wire representation.
 */
export function toYesNo(value: boolean): "Y" | "N" {
  return value ? "Y" : "N";
}

/**
 * Merges one Verified Information string field with its saved fallback: a
 * non-blank verified value wins, otherwise the saved value.
 * @param {string} verifiedValue - The analyst-entered value.
 * @param {string} savedValue - The saved baseline.
 * @returns {string} The merged value, trimmed; "" when both are blank.
 */
export function mergeString(verifiedValue: string, savedValue: string): string {
  const trimmed = verifiedValue.trim();
  return trimmed === "" ? savedValue.trim() : trimmed;
}

/**
 * Same as {@link mergeString}, but returns `undefined` instead of "" so the
 * key is omitted from the request body.
 * @param {string} verifiedValue - The analyst-entered value.
 * @param {string} savedValue - The saved baseline.
 * @returns {string | undefined} The merged value, or undefined to omit the field.
 */
export function mergeOptionalString(verifiedValue: string, savedValue: string): string | undefined {
  const merged = mergeString(verifiedValue, savedValue);
  return merged === "" ? undefined : merged;
}

/**
 * Same merge for the currency/number rows, which carry `number | ""`.
 * @param {number | ""} verifiedValue - The analyst-entered value.
 * @param {number | ""} savedValue - The saved baseline.
 * @returns {number | undefined} The merged value, or undefined to omit the field.
 */
export function mergeOptionalNumber(verifiedValue: number | "", savedValue: number | ""): number | undefined {
  if (verifiedValue !== "") {
    return verifiedValue;
  }
  return savedValue === "" ? undefined : savedValue;
}
